/**
 * Client for team scheduling: assignee suggestions, the caller's team-energy sharing choice,
 * and the closed set of meeting reasons. The server owns every score; these responses carry
 * an order and number-free reasons only. Mirrors `crates/domain/src/team_scheduling.rs`.
 *
 * @module apps/web/src/lib/teamScheduling
 */
import { request } from '@/lib/api';

/** The note every suggestion surface shows next to the ranked list. */
export const TEAM_SCHEDULING_NOTE =
  "Suggestions use teammates' availability and, if they allow it, their energy patterns.";

/** Coarse kinds of work, as the API names them. */
export const ACTIVITY_KINDS = [
  { value: 'deep_work', label: 'Deep work' },
  { value: 'admin', label: 'Admin' },
  { value: 'meetings', label: 'Meetings' },
  { value: 'creative', label: 'Creative' },
  { value: 'physical', label: 'Physical' },
  { value: 'errands', label: 'Errands' },
  { value: 'learning', label: 'Learning' },
  { value: 'rest', label: 'Rest' },
] as const;

/** One of the activity kind values the API accepts. */
export type ActivityKindValue = (typeof ACTIVITY_KINDS)[number]['value'];

/** Every reason text a meeting candidate may carry; anything else is dropped. */
export const MEETING_REASONS = [
  'Available for the requested duration.',
  "Inside everyone's working hours.",
  "Outside someone's working hours.",
  'Comfortable local times for everyone.',
  'Good energy overlap.',
] as const;

/** An activity draft sent to ask who should do it; it carries no personal energy. */
export interface AssigneeSuggestionDraft {
  /** Activity title, 1-500 characters. */
  title: string;
  /** Optional notes. */
  description?: string | null;
  /** Expected minutes, 5-1440; the server defaults to 45. */
  duration_minutes?: number;
  /** RFC 3339 window start; the server defaults to now. */
  window_start?: string;
  /** RFC 3339 window end; the server defaults to seven days after the start. */
  window_end?: string;
  /** The kind of work, when known. */
  kind?: ActivityKindValue;
}

/** One ranked member; never includes scores or energy values. */
export interface AssigneeSuggestion {
  /** Team membership id. */
  member_id: string;
  /** Account the activity would be assigned to. */
  user_id: string;
  /** Display name or "Team member". */
  display_name: string;
  /** One-based position in the suggested order. */
  rank: number;
  /** Whether the member has an open stretch long enough in the window. */
  available: boolean;
  /** One or two display texts with no numbers. */
  reason_texts: string[];
}

/** The suggestion endpoint's payload. */
export interface AssigneeSuggestions {
  /** Every active linked member, best first. */
  suggestions: AssigneeSuggestion[];
  /** The fixed explanatory note. */
  note: string;
}

/** The caller's own team-energy sharing state. */
export interface TeamEnergySharingState {
  /** Whether team scheduling may use the caller's energy patterns. */
  enabled: boolean;
  /** Always true: sharing is on by default under the Terms of Use. */
  defaultEnabled: boolean;
  /** `default` until the person chooses, then `choice`. */
  source: 'default' | 'choice';
  /** Terms version the default comes from. */
  consentVersion: string;
}

/** Keep only texts with no digits, so no number can reach the screen even from a faulty server. */
export function numberFreeReasons(texts: unknown): string[] {
  return Array.isArray(texts)
    ? texts.filter((text): text is string => typeof text === 'string' && !/\d/.test(text))
    : [];
}

/** Keep only known meeting reasons; fall back to the availability reason. */
export function meetingReasons(texts: unknown): string[] {
  const known = new Set<string>(MEETING_REASONS);
  const kept = Array.isArray(texts)
    ? texts.filter((text): text is string => typeof text === 'string' && known.has(text))
    : [];
  return kept.length ? kept : [MEETING_REASONS[0]];
}

/**
 * Build a draft window from an optional local due date: now until the due date when it is in
 * the future and within 31 days, otherwise the server default.
 */
export function draftWindow(dueDate: string, now = new Date()): Pick<AssigneeSuggestionDraft, 'window_start' | 'window_end'> {
  const due = dueDate ? new Date(dueDate) : null;
  const limit = now.getTime() + 31 * 86_400_000;
  if (!due || Number.isNaN(due.getTime()) || due <= now || due.getTime() > limit) return {};
  return { window_start: now.toISOString(), window_end: due.toISOString() };
}

/** Team scheduling API calls; each throws the server's message on failure. */
export const teamSchedulingApi = {
  /** POST the draft and return ranked members (managers only). */
  async suggestAssignees(teamId: string, draft: AssigneeSuggestionDraft): Promise<AssigneeSuggestions> {
    const response = await request<AssigneeSuggestions>(
      `/teams/${encodeURIComponent(teamId)}/activities/assignee-suggestions`,
      { method: 'POST', body: JSON.stringify(draft) }
    );
    if (!response.success || !response.data)
      throw new Error(response.error?.message || 'Suggestions are unavailable right now.');
    return response.data;
  },
  /** GET the caller's sharing state. */
  async getSharing(): Promise<TeamEnergySharingState> {
    const response = await request<TeamEnergySharingState>('/user/me/team-energy-sharing');
    if (!response.success || !response.data)
      throw new Error(response.error?.message || 'Could not load this setting.');
    return response.data;
  },
  /** PUT the caller's sharing choice. */
  async setSharing(enabled: boolean): Promise<TeamEnergySharingState> {
    const response = await request<TeamEnergySharingState>('/user/me/team-energy-sharing', {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    });
    if (!response.success || !response.data)
      throw new Error(response.error?.message || 'Could not save this setting.');
    return response.data;
  },
};
